import React, { useState, useEffect } from "react";
import { 
  Send, 
  History, 
  Search, 
  RefreshCw, 
  Terminal, 
  CheckCircle2, 
  AlertTriangle, 
  X,
  Smartphone,
  Clock,
  Activity
} from "lucide-react";
import axios from "../api/axios";

const DeviceCommands = () => {
  const [imei, setImei] = useState("");
  const [command, setCommand] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchImei, setSearchImei] = useState("");

  const fetchHistory = async (targetImei) => {
    if (!targetImei) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/commands/history/${targetImei}`);
      setHistory(response.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Không thể tải lịch sử lệnh");
    } finally {
      setLoading(false);
    }
  };

  const handleSendCommand = async (e) => {
    e.preventDefault();
    if (!imei || !command) {
      setError("Vui lòng nhập đầy đủ IMEI và lệnh");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await axios.post("/commands/send", { imei, command });
      setSuccessMsg("Lệnh đã được gửi thành công!");
      setCommand(""); // Reset command field
      fetchHistory(imei); // Refresh history for this device
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Lỗi khi gửi lệnh");
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (commandId, status) => {
    try {
      await axios.patch(`/commands/status/${commandId}`, { status });
      if (imei) fetchHistory(imei);
    } catch (err) {
      setError("Không thể cập nhật trạng thái lệnh");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-4 md:p-6 font-sans">
      <div className="max-w-[98%] mx-auto">
        
        {/* Header Section */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <Terminal className="text-blue-600" size={28} />
              Điều Khiển Thiết Bị
            </h1>
            <p className="text-gray-500 text-sm font-medium italic tracking-tight">Gửi lệnh và theo dõi trạng thái thiết bị</p>
          </div>
        </div>

        {/* Global Messages */}
        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex justify-between items-center text-emerald-700 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} />
              <span>{successMsg}</span>
            </div>
            <button onClick={() => setSuccessMsg(null)}><X size={18}/></button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex justify-between items-center text-rose-600 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)}><X size={18}/></button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Send Command Form */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 sticky top-6">
              <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
                <Send size={20} className="text-blue-600" />
                GỬI LỆNH MỚI
              </h2>
              
              <form onSubmit={handleSendCommand} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Số IMEI</label>
                  <div className="relative">
                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1-2 text-gray-300" size={20} />
                    <input 
                      type="text" 
                      value={imei}
                      onChange={(e) => setImei(e.target.value)}
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-mono font-bold text-gray-700" 
                      placeholder="Nhập IMEI..." 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Nội dung lệnh</label>
                  <div className="relative">
                    <Terminal className="absolute left-4 top-6 text-gray-300" size={20} />
                    <textarea 
                      value={command}
                      onChange={(e) => setCommand(e.target.value)}
                      rows="4"
                      className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-mono font-bold text-gray-700 resize-none" 
                      placeholder="Ví dụ: RESET, STATUS..." 
                    />
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={sending}
                  className="w-full py-4 bg-blue-600 text-white font-black rounded-2xl shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {sending ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <Send size={20} />
                  )}
                  GỬI LỆNH
                </button>
              </form>
            </div>
          </div>

          {/* Command History */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <History size={20} className="text-blue-600" />
                  LỊCH SỬ LỆNH
                </h2>
                
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                  <input 
                    type="text" 
                    value={searchImei}
                    onChange={(e) => setSearchImei(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchHistory(searchImei)}
                    placeholder="Tìm theo IMEI..." 
                    className="w-full pl-11 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                  />
                  <button 
                    onClick={() => fetchHistory(searchImei)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Search size={16} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <RefreshCw size={40} className="animate-spin text-blue-500 mb-4 opacity-50" />
                    <p className="font-black text-gray-400 uppercase text-xs tracking-widest">Đang tải dữ liệu...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <Activity size={48} className="mb-4 opacity-20" />
                    <p className="font-bold text-sm">Không có lịch sử lệnh cho thiết bị này</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Lệnh</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Thời gian</th>
                        <th className="px-6 py-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Trạng thái</th>
                        <th className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Phản hồi</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-50">
                      {history.map((item, idx) => (
                        <tr key={item.id || idx} className="hover:bg-blue-50/20 transition-all group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gray-100 text-gray-600 rounded-lg">
                                <Terminal size={16} />
                              </div>
                              <span className="text-sm font-bold text-gray-900 font-mono">{item.command}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-gray-500">
                              <Clock size={14} />
                              <span className="text-xs font-medium">{formatDate(item.created_at)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase ring-1 ${
                              item.status === 'success' ? 'bg-emerald-50 text-emerald-600 ring-emerald-100' :
                              item.status === 'pending' ? 'bg-amber-50 text-amber-600 ring-amber-100' :
                              'bg-rose-50 text-rose-600 ring-rose-100'
                            }`}>
                              {item.status || 'UNKNOWN'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-xs text-gray-400 font-medium italic">
                              {item.response || "Chưa có phản hồi"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceCommands;
